import React, { useEffect, useState } from "react";
import ShadowHaltAlert, { useShadowHalt } from "@/components/ShadowHaltAlert";
import { NavLink, useLocation } from "react-router-dom";
import { useAdvanced } from "@/lib/advanced";
import {
  LayoutDashboard, Table2, Wrench, Activity, ShieldAlert, BookOpen,
  PlugZap, Bell, LogOut, TrendingUp, Power, X, Zap, Crosshair,
  Library, FlaskConical, Users as UsersIcon, UserCircle2,
  PanelLeftClose, PanelLeftOpen, Radar, Sun, Moon,
  SlidersHorizontal, Layers, ClipboardCheck, History, Settings2, Package, LayoutList, Cog, Gauge, Lock,
} from "lucide-react";
import { IRON_CONDOR_ENABLED, OPTIONS_ENABLED, OPTIONS_HEDGE_ENABLED } from "@/lib/optionsApi";
import { useProducts, OPTIONS } from "@/lib/products";
import { MODULE, isActive, useModules } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { inr } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { P } from "@/lib/permissions";
import { useTheme } from "@/context/ThemeContext";
import AccountSummaryBar from "@/components/AccountSummaryBar";
import { sharedGet, visibleInterval } from "@/lib/poll";
import { marketBadge, headerWarnings, TONE_CLASS } from "@/lib/statusModel";

const SIDEBAR_KEY = "gz_sidebar_collapsed";
function readCollapsed() {
  try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
}


// Each item declares the permission it needs; items the user lacks are not shown. `product` items
// are shown only when the account's trading products include it (FUTURES_ONLY / OPTIONS_ONLY / BOTH).
// One trading platform, not separate engines: MARKETS (find a trade), TRADE (start / manage it),
// PORTFOLIO (Active Trades + Trading Book: positions, orders, fills, P&L, history - common to Futures
// and Options, filtered by product and environment).
// Redesign stage 1 (23 Oct 2026): the terminal's groups. Every entry is an existing page (or says honestly that it
// is not built yet). `module` = the activation it needs (lib/modules): a module the account lacks stays listed with a
// lock and opens the "Module not activated" page. `perm` = the role it needs: entries the role lacks are not shown.
const NAV_GROUPS = [
  { section: null, items: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", perm: P.TRADING_VIEW },
    { to: "/explorer", module: MODULE.FUTURES, icon: Table2, label: "Market Watch", perm: P.TRADING_VIEW, full: "Market Watch (Futures Explorer)" },
    { to: "/options/chain", module: MODULE.ANALYSIS, product: OPTIONS, icon: Layers, label: "Option Chain", perm: P.OPTIONS_VIEW },
  ] },
  { section: "SCANNER TERMINAL", items: [
    { to: "/options/scanner", module: MODULE.ANALYSIS, product: OPTIONS, icon: SlidersHorizontal, label: "Option Trade Scanner", perm: P.OPTIONS_VIEW },
    { to: "/opportunities", module: MODULE.FUTURES, icon: Crosshair, label: "Future Trade Scanner", perm: P.TRADING_VIEW },
    ...(IRON_CONDOR_ENABLED ? [
      { to: "/options/iron-condor?scan=IRON_CONDOR", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Radar, label: "Condor Scanner", perm: P.OPTIONS_VIEW },
      { to: "/options/iron-condor?scan=BOX", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Package, label: "Box Scanner", perm: P.OPTIONS_VIEW },
    ] : []),
    { to: "/options/settings", module: MODULE.ANALYSIS, product: OPTIONS, icon: Settings2, label: "Scanner Settings", perm: P.OPTIONS_VIEW },
    { to: "/options/history", module: MODULE.ANALYSIS, product: OPTIONS, icon: History, label: "Scanner History", perm: P.OPTIONS_VIEW },
    { to: "/options/diagnostics", module: MODULE.ANALYSIS, product: OPTIONS, icon: ClipboardCheck, label: "Options Diagnostics", perm: P.OPTIONS_VIEW, more: true, advanced: true },
  ] },
  { section: "STRATEGY BUILDER", items: [
    { to: "/options/builder", module: MODULE.HEDGE, product: OPTIONS, icon: Wrench, label: "Option Builder", perm: P.OPTIONS_VIEW },
    { to: "/futures/builder", module: MODULE.FUTURES, icon: Wrench, label: "Future Builder", perm: P.TRADING_VIEW },
    ...(IRON_CONDOR_ENABLED ? [
      { to: "/options/iron-condor/builder?kind=IRON_CONDOR", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Wrench, label: "Condor Builder", perm: P.OPTIONS_VIEW },
      { to: "/options/iron-condor/builder?kind=BOX", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Wrench, label: "Box Builder", perm: P.OPTIONS_VIEW },
    ] : []),
    { to: "/strategy-library", module: MODULE.FUTURES, icon: Library, label: "Strategy Library", perm: P.TRADING_VIEW },
  ] },
  { section: "TRADE TERMINAL", items: [
    { to: "/trades", icon: LayoutList, label: "Common Trade Panel", perm: P.TRADING_VIEW },
    ...(OPTIONS_HEDGE_ENABLED ? [{ to: "/options/hedge", module: MODULE.HEDGE, product: OPTIONS, icon: Layers, label: "Option Trade", perm: P.OPTIONS_TRADE, badge: "hedge" }] : []),
    { to: "/paper", module: MODULE.FUTURES, icon: Zap, label: "Future Trade", perm: P.TRADING_VIEW },
    ...(IRON_CONDOR_ENABLED ? [
      { to: "/options/iron-condor/trades?kind=IRON_CONDOR", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Radar, label: "Condor Trade", perm: P.OPTIONS_VIEW },
      { to: "/options/iron-condor/trades?kind=BOX", module: MODULE.IRON_CONDOR, product: OPTIONS, icon: Package, label: "Box Trade", perm: P.OPTIONS_VIEW },
    ] : []),
  ] },
  { section: "PORTFOLIO", items: [
    { to: "/active-trades", icon: Activity, label: "Active Trades", perm: P.TRADING_VIEW },
    { to: "/book", end: true, icon: BookOpen, label: "Trading Book", perm: P.TRADING_VIEW },
    { to: "/book?tab=history", icon: History, label: "Trade History", perm: P.TRADING_VIEW },
    // Broker validation: REAL orders to an UNFUNDED account, expected to be REJECTED, with
    // the strategy advancing on a simulated fill. Labelled everywhere it appears.
    { to: "/shadow-live", icon: Radar, label: "Shadow Live", perm: P.TRADING_LIVE },
  ] },
  { section: "ACCOUNT & SYSTEM", items: [
    { to: "/broker", icon: PlugZap, label: "Broker Session", perm: P.BROKER },
    { to: "/account", icon: UserCircle2, label: "My Account" },
    { to: "/settings", icon: Cog, label: "Settings" },
    { to: "/system-health", icon: Gauge, label: "System Health" },
  ] },
  { section: "ADMIN", items: [
    { to: "/admin/overview", icon: LayoutDashboard, label: "Overview", perm: P.USERS_READ },
    { to: "/admin/users", icon: UsersIcon, label: "Users", perm: P.USERS_READ },
  ] },
];

// Development-era screens (Simulation, Old Strategy Builder, Legacy Journal, Risk Centre,
// Legacy Dashboard) are deliberately NOT in the trader navigation. Their routes still exist
// in App.js and can be reached directly by URL, or listed here by setting
// REACT_APP_SHOW_DEV_NAV=true.
const DEV_NAV_GROUP = {
  section: "ADMIN / DEV", items: [
    { to: "/strategies", icon: FlaskConical, label: "Simulation", perm: P.SIMULATOR },
    { to: "/builder", icon: Wrench, label: "Old Strategy Builder", perm: P.SIMULATOR },
    { to: "/journal", icon: BookOpen, label: "Legacy Journal", perm: P.SIMULATOR },
    { to: "/risk", icon: ShieldAlert, label: "Risk Centre", perm: P.SIMULATOR },
    { to: "/dev/legacy-dashboard", icon: LayoutDashboard, label: "Legacy Dashboard", perm: P.SIMULATOR },
  ],
};
const SHOW_DEV_NAV = process.env.REACT_APP_SHOW_DEV_NAV === "true";
const MORE_KEY = "gz.nav.optionsMore";

/** Active Options Hedging trades for the nav badge, and whether one needs a human. */
function useHedgeBadge(enabled) {
  const [b, setB] = useState({ active: 0, attention: false });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/options-hedge/trades").then((r) => {
      const rows = (r.data?.trades || []).filter((t) => !["COMPLETE", "STOPPED", "DRAFT", "READY"].includes(t.status));
      if (alive) setB({ active: rows.length, attention: rows.some((t) => t.status === "ENTRY_STOPPED_UNHEDGED"
        || (t.recovery_state && t.recovery_state !== "ACKNOWLEDGED")) });
    }).catch(() => {});
    load();
    const i = setInterval(() => { if (!document.hidden) load(); }, 20000);
    return () => { alive = false; clearInterval(i); };
  }, [enabled]);
  return b;
}
const WITH_OPTIONS = OPTIONS_ENABLED ? NAV_GROUPS
  : NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => n.product !== OPTIONS) }));
const NAV = SHOW_DEV_NAV ? [...WITH_OPTIONS, DEV_NAV_GROUP] : WITH_OPTIONS;

function StatusDot({ ok, label }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`status-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-[#00C805]" : "bg-[#475569]"}`} />
      <span className={`text-[11px] ${ok ? "text-slate-300" : "text-slate-500"}`}>{label}</span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const canTradeRole = can(P.TRADING_VIEW);
  const [advanced] = useAdvanced();
  const [moreOpen, setMoreOpen] = useState(() => { try { return localStorage.getItem(MORE_KEY) === "1"; } catch { return false; } });
  const toggleMore = () => setMoreOpen((v) => { try { localStorage.setItem(MORE_KEY, v ? "0" : "1"); } catch { /* optional */ } return !v; });
  const products = useProducts();
  const futuresOn = products.futures;
  const canTrade = canTradeRole && futuresOn;              // the futures header reads below
  const hedgeBadge = useHedgeBadge(OPTIONS_HEDGE_ENABLED && products.options && can(P.OPTIONS_TRADE));
  const mods = useModules();
  const loc = useLocation();
  const navGroups = NAV
    .map((g) => ({ ...g, items: g.items.filter((n) => (!n.perm || can(n.perm)) && (!n.advanced || advanced)) }))
    .filter((g) => g.items.length > 0);
  // active = same path AND (when the entry has one) the same query: Condor Trade and Box Trade share a path.
  // An entry without a query yields to a sibling whose query matches (/paper vs /paper?new=1).
  const queryMatches = (query) => {
    const want = new URLSearchParams(query); const have = new URLSearchParams(loc.search);
    return [...want.keys()].every((k) => have.get(k) === want.get(k));
  };
  const isCurrent = (n) => {
    const [path, query] = n.to.split("?");
    const samePath = loc.pathname === path || (!n.end && !query && loc.pathname.startsWith(`${path}/`));
    if (!samePath) return false;
    if (query) return queryMatches(query);
    return !navGroups.some((g) => g.items.some((x) => x !== n && x.to.startsWith(`${path}?`) && queryMatches(x.to.split("?")[1])));
  };

  const { broker, unread, notifications, refreshNotifications, zerodha, systemStatus, config, environment, setEnvironment, canSim } = useApp();
  const [showNotif, setShowNotif] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleSidebar = () => setCollapsed((c) => {
    try { localStorage.setItem(SIDEBAR_KEY, c ? "0" : "1"); } catch { /* preference is optional */ }
    return !c;
  });
  const [paperSummary, setPaperSummary] = useState({ total: 0, running: 0, complete: 0, exiting: 0, openTrades: 0,
    openLevels: 0, openPairs: 0, used: 0, allocated: 0, unrealised: 0, realised: 0, parity: null, blocked: 0 });
  const [showWarnings, setShowWarnings] = useState(false);
  const [allTrades, setAllTrades] = useState([]);
  const [accountSummary, setAccountSummary] = useState(null);

  // Compact PAPER summary derived from the real trade instances (both markets).
  useEffect(() => {
    if (environment !== "PAPER" || !canTradeRole) return undefined;
    // Every environment is fetched (the account summary shows LIVE / SHADOW / PAPER by the
    // account's mode); the PAPER summary below — alerts and engine health — stays PAPER-only.
    const load = () => sharedGet("/paper/v25/trades", { params: { market: "ALL", environment: "ALL" } })
      .then((r) => {
        const all = r.data.trades || [];
        setAllTrades(all);
        const t = all.filter((x) => (x.environment || x.trading_environment || "PAPER") === "PAPER");
        const sum = (f) => t.reduce((a, x) => a + (f(x) || 0), 0);
        setPaperSummary({
          total: t.length,
          running: t.filter((x) => x.status === "RUNNING").length,
          complete: t.filter((x) => x.status === "COMPLETE").length,
          exiting: t.filter((x) => x.status === "EXITING").length,
          openTrades: t.filter((x) => (x.open_levels || 0) > 0).length,
          openLevels: sum((x) => x.open_levels),
          openPairs: sum((x) => (x.pair_execution_enabled ? x.open_hedged_pairs : 0)),
          used: sum((x) => x.paper_capital_used ?? x.funds_used),
          allocated: sum((x) => x.paper_allocated_capital ?? x.allocated_funds),
          unrealised: sum((x) => x.unrealised_pnl),
          realised: sum((x) => x.realised_pnl),
          parity: r.data.parity?.passed ?? null,
          blocked: t.filter((x) => x.status === "RUNNING" && (x.next_level_detail?.wait_reason || "").startsWith("BLOCKED")).length,
        });
      }).catch(() => {});
    // The caller's OWN broker account mode + Zerodha funds (display only).
    const loadAccount = () => sharedGet("/account/summary")
      .then((r) => setAccountSummary(r.data)).catch(() => setAccountSummary(null));
    if (futuresOn) load(); loadAccount();
    const stops = [visibleInterval(loadAccount, 5000)];
    if (futuresOn) stops.push(visibleInterval(load, 5000));
    return () => stops.forEach((stop) => stop());
  }, [environment, canTradeRole, futuresOn]);
  // ONE status (GET /system-status via AppContext, worded by lib/statusModel): until it has loaded nothing
  // claims connected / disconnected / live - the header says "checking" instead of showing a wrong state.
  const statusLoaded = !!systemStatus;
  const zConnected = zerodha?.status === "CONNECTED";
  const badge = marketBadge(systemStatus);
  const marketLive = badge?.feed === "LIVE";
  const marketClosed = badge?.feed === "CLOSED";
  const st = broker?.states || {};
  // Engine health = what the paper engine itself requires before evaluating any trade:
  // parity gate passed, Zerodha connected and market data OK. (Worker count is not exposed by the API.)
  const engineOk = paperSummary.parity === true && zConnected && marketLive;
  const shadowHalt = useShadowHalt(canTradeRole);
  const warnings = headerWarnings(systemStatus, [
    shadowHalt && { code: "SHADOW_HALTED", severity: "critical", text: `SHADOW halted — ${shadowHalt.reason || "see Shadow Live"}` },
    futuresOn && environment === "PAPER" && paperSummary.parity === false
      && { code: "PARITY_FAILED", severity: "critical", text: "Parity gate FAILED — PAPER futures entries blocked" },
    environment === "PAPER" && paperSummary.exiting > 0
      && { code: "EXITING", severity: "info", text: `${paperSummary.exiting} futures trade(s) exiting` },
  ]);

  const markRead = async () => {
    await api.post("/notifications/read-all");
    refreshNotifications();
  };

  const doExitAll = async () => {
    if (!window.confirm("EXIT ALL AND STOP: square off every strategy and stop automation?")) return;
    try { await api.post("/broker/exit-all"); toast.warning("Exit All & Stop executed"); }
    catch (e) { toast.error("Exit all failed"); }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0C10] text-slate-100">
      {/* Sidebar */}
      <aside className={`terminal-sidebar ${collapsed ? "w-14" : "w-56"} shrink-0 sticky top-0 h-screen border-r border-[#2A2F3A] bg-[#12151C] flex flex-col transition-[width] duration-150`}
        data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={`h-14 flex items-center border-b border-[#2A2F3A] ${collapsed ? "flex-col justify-center gap-0.5" : "gap-2 px-4"}`}>
          <TrendingUp className="h-5 w-5 text-[#2563EB] shrink-0" />
          {!collapsed && (
            <div className="sidebar-label leading-tight flex-1 min-w-0">
              <div className="text-sm font-bold tracking-tight">GZ</div>
              <div className="text-[10px] text-slate-500 mono">TRADING</div>
            </div>
          )}
          {/* collapse / expand sits at the top, where it is found (it used to hide at the bottom of a long menu) */}
          <button onClick={toggleSidebar} data-testid="sidebar-toggle"
            title={collapsed ? "Expand menu" : "Collapse menu"} aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            className={`${collapsed ? "h-6 w-8" : "h-8 w-8"} rounded-sm flex items-center justify-center text-[var(--term-muted)] hover:text-[var(--term-primary)] hover:bg-[var(--term-hover)]`}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        <nav className="flex-1 min-h-0 py-1.5 overflow-y-auto overflow-x-hidden">
  {!products.loaded && (
    <div className="px-4 py-2 space-y-3" data-testid="nav-loading" aria-label="Loading navigation">
      {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-3 rounded-sm bg-[var(--term-s3)] animate-pulse" />)}
    </div>
  )}
  {products.loaded && navGroups.map((g, gi) => (
    <div key={g.section || `top-${gi}`}>
      {g.section && (
        collapsed
          ? <div className="mx-3 my-2 border-t border-[var(--term-border)]" />
          : (
            <div className="sidebar-label px-4 pt-2.5 pb-1 text-[10px] font-bold tracking-wide text-[var(--term-muted)] whitespace-nowrap truncate">
              {g.section}
            </div>
          )
      )}

      {g.items.filter((n) => !n.more || moreOpen).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={!!n.end}
          data-testid={`nav-${n.to.slice(1)}`}
          title={`${n.full || n.label}${isActive(mods, n.module) ? "" : " (module not activated)"}`}
          aria-label={n.full || n.label}
          className={() =>
            `flex items-center ${
              collapsed ? "justify-center px-0" : "gap-3 px-4"
            } h-9 text-[13px] leading-none border-l-2 transition-colors duration-150 ${
              isCurrent(n)
                ? "border-[var(--gz-blue)] bg-[var(--term-s3)] text-[var(--term-primary)]"
                : "border-transparent text-[var(--term-secondary)] hover:bg-[var(--term-hover)] hover:text-[var(--term-primary)]"
            }`
          }
        >
          <n.icon className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="sidebar-label min-w-0 truncate whitespace-nowrap">{n.label}</span>
          )}
          {!collapsed && !isActive(mods, n.module) && (
            <Lock className="ml-auto mr-1 h-3 w-3 shrink-0 text-[var(--term-muted)]" aria-label="Module not activated" data-testid={`nav-lock-${n.label.replace(/\s+/g, "-").toLowerCase()}`} />
          )}
          {n.badge === "hedge" && hedgeBadge.active > 0 && (
            <span className={`ml-auto mr-1 min-w-4 h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${hedgeBadge.attention ? "bg-[#FF3B30] text-white" : "bg-[var(--term-s3)] text-[var(--term-primary)]"}`}
              data-testid="nav-hedge-badge" title={hedgeBadge.attention ? "A trade needs your attention" : "Active options trades"}>{hedgeBadge.active}</span>
          )}
        </NavLink>
      ))}
      {g.items.some((n) => n.more) && !collapsed && (
        <button onClick={toggleMore} data-testid="nav-options-more"
          className="flex items-center gap-3 pl-11 pr-4 h-7 text-xs text-[var(--term-muted)] hover:text-[var(--term-primary)] w-full">
          {moreOpen ? "Less" : "More…"}
        </button>
      )}
    </div>
  ))}
</nav>
        {canSim && environment === "SIMULATION" && !collapsed && (
          <div className="p-3 border-t border-[#2A2F3A] space-y-1" data-testid="sim-broker-states">
            <StatusDot ok={st.broker_connected} label="Broker" />
            <StatusDot ok={st.market_data_connected} label="Market Data" />
            <StatusDot ok={st.order_stream_connected} label="Order Stream" />
            <StatusDot ok={st.funds_synced} label="Funds Synced" />
            <StatusDot ok={st.automation_ready} label="Automation Ready" />
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="min-h-14 py-2 shrink-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-3 border-b border-[#2A2F3A] bg-[#12151C] min-w-0" data-testid="app-header">
          {/* ── System status ── (wraps onto a second line instead of pushing the page sideways) */}
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {!statusLoaded && <span data-testid="status-loading" className="px-2 py-1 rounded-sm text-[10px] font-bold border bg-[#1A1D24] text-slate-500 border-[#2A2F3A] whitespace-nowrap">CHECKING STATUS…</span>}
            {futuresOn && canSim && <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]" data-testid="env-badge">
              {(canSim ? ["SIMULATION", "PAPER"] : ["PAPER"]).map((e) => (
                <button key={e} data-testid={`env-select-${e.toLowerCase()}`} onClick={() => setEnvironment(e)}
                  className={`px-2 py-1 text-[10px] font-bold flex items-center gap-1 ${environment === e ? (e === "PAPER" ? "bg-[#00C805] text-black" : "bg-[#2563EB] text-white") : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>
                  {e === environment && <Zap className="h-3 w-3" />}{e === "SIMULATION" ? "MOCK DATA" : "ZERODHA DATA"}
                </button>
              ))}
            </div>}
            {futuresOn && (environment !== "PAPER" || !!badge) && <span data-testid="market-data-badge"
              title={environment === "PAPER" ? badge.title : "SIMULATION uses mock prices"}
              className={`px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${environment === "PAPER" ? TONE_CLASS[badge.tone] : TONE_CLASS.idle}`}>
              {environment === "PAPER" ? badge.text : "MARKET DATA MOCK"}
            </span>}
            {statusLoaded && environment === "PAPER" && canTrade && (
              <span data-testid="engine-badge" title="Trading engine prerequisites: parity gate, Zerodha session, live market data"
                className={`hidden lg:inline px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${engineOk ? "bg-[#00C805]/10 text-[#00C805] border-[#00C805]/30" : marketClosed && paperSummary.parity !== false ? TONE_CLASS.idle : "bg-[#FF3B30]/15 text-[#FF6b62] border-[#FF3B30]/40"}`}>
                ENGINE {engineOk ? "READY" : paperSummary.parity === false ? "BLOCKED (PARITY)" : marketClosed ? "IDLE · MARKETS CLOSED" : "WAITING FOR DATA"}
              </span>
            )}
            {/* LIVE-10. The composite, in the header: every component must hold. The title lists
                exactly what is missing, so "NOT READY" is never a mystery. */}
            {canTrade && systemStatus?.live_readiness && (
              <span data-testid="live-ready-badge"
                title={systemStatus.live_readiness.live_execution_ready
                  ? "Connected, fresh market data, funds/orders/positions synced, reconciled, engine running"
                  : "Missing: " + (systemStatus.live_readiness.blocking || []).join(", ").replace(/_/g, " ")}
                className={`hidden xl:inline px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${
                  systemStatus.live_readiness.live_execution_ready
                    ? "bg-[#00C805]/10 text-[#00C805] border-[#00C805]/30"
                    : "bg-[#1A1D24] text-slate-400 border-[#2A2F3A]"}`}>
                LIVE {systemStatus.live_readiness.live_execution_ready ? "READY" : "NOT READY"}
              </span>
            )}
            {/* RECONCILED / MISMATCH / STALE, straight from the state the risk gate reads. */}
            {canTrade && systemStatus?.live_readiness?.reconciliation_state && (
              <span data-testid="recon-badge"
                title="A mismatch blocks NEW ENTRIES for the account. Exits, square-off and monitoring keep running."
                className={`hidden xl:inline px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${
                  systemStatus.live_readiness.reconciliation_state === "RECONCILED"
                    ? "bg-[#00C805]/10 text-[#00C805] border-[#00C805]/30"
                    : systemStatus.live_readiness.reconciliation_state === "MISMATCH"
                      ? "bg-[#FF3B30]/15 text-[#FF6b62] border-[#FF3B30]/40"
                      : "bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/30"}`}>
                {systemStatus.live_readiness.reconciliation_state}
              </span>
            )}
            {futuresOn && <span data-testid="live-badge" title="Real-money order placement is server-flag gated"
              className={`hidden lg:inline px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${config?.live_trading_enabled ? "bg-[#FF3B30]/15 text-[#FF6b62] border-[#FF3B30]/40" : "bg-[#1A1D24] text-slate-400 border-[#2A2F3A]"}`}>
              LIVE TRADING {config?.live_trading_enabled ? "ON" : "OFF"}
            </span>}

            <div data-testid="zerodha-chip" className={`hidden ${futuresOn ? "2xl:flex" : "lg:flex"} items-center gap-1 text-[11px] whitespace-nowrap ${!statusLoaded ? "text-slate-500" : zConnected ? "text-[#00C805]" : zerodha?.status === "EXPIRED" || zerodha?.status === "ERROR" ? "text-[#FF6b62]" : "text-[#FFCC00]"}`}>
              <span className={`h-2 w-2 rounded-full ${zConnected ? "bg-[#00C805]" : zerodha?.status === "EXPIRED" || zerodha?.status === "ERROR" ? "bg-[#FF3B30]" : "bg-[#FFCC00]"}`} />
              {!statusLoaded ? "Zerodha …" : zConnected ? `Zerodha ${zerodha.broker_user_id || ""}` : `Zerodha ${zerodha?.status || "DISCONNECTED"}`}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto min-w-0">
{/* PAPER: every trade carries its OWN independent simulated capital, so a single
                shared Free/Used pool would be misleading. The account summary shows the user's OWN
                Zerodha funds apart from GroundZero's trade totals, in the account's mode
                (LIVE / SHADOW / PAPER) — see AccountSummaryBar. */}
            {!canTradeRole ? null : environment === "PAPER" ? (
              <div className="min-w-0 flex-1 overflow-hidden" data-testid="paper-capital-summary">
                {accountSummary && <AccountSummaryBar summary={accountSummary} trades={futuresOn ? allTrades : []} collapsed={collapsed} />}
              </div>
            ) : broker?.connected && (
              <div className="hidden md:flex items-center gap-4 text-xs">
                <div><span className="text-slate-500">Free </span><span className="mono text-slate-100">{inr(broker.free_margin)}</span></div>
                <div><span className="text-slate-500">Used </span><span className="mono text-slate-100">{inr(broker.used_margin)}</span></div>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="relative shrink-0">
                <button type="button" data-testid="warning-count" onClick={() => setShowWarnings((v) => !v)} aria-expanded={showWarnings}
                  title="Show the warnings"
                  className={`px-2 py-1 rounded-sm text-[10px] font-bold border whitespace-nowrap ${warnings.some((w) => w.severity === "critical")
                    ? "bg-[#FF3B30]/15 text-[#FF6b62] border-[#FF3B30]/40" : "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/40"}`}>
                  ⚠ {warnings.length} WARNING{warnings.length > 1 ? "S" : ""}
                </button>
                {showWarnings && (
                  <div className="absolute right-0 mt-2 w-80 max-w-[90vw] term-panel-2 rounded-sm z-50 shadow-2xl" data-testid="warnings-list" role="dialog" aria-label="Warnings">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#2A2F3A]">
                      <span className="text-xs font-bold">Warnings</span>
                      <button onClick={() => setShowWarnings(false)} aria-label="Close warnings"><X className="h-3.5 w-3.5 text-slate-500" /></button>
                    </div>
                    {warnings.map((w) => (
                      <div key={w.code} className="px-3 py-2 border-b border-[#20242c] text-xs flex gap-2" data-testid={`warning-${w.code}`}>
                        <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${w.severity === "critical" ? "bg-[#FF3B30]" : w.severity === "warning" ? "bg-[#FF9500]" : "bg-[#2563EB]"}`} />
                        <span className="text-slate-200">{w.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* EXIT ALL & STOP acts on the SIMULATION strategies (mock broker) — ADMIN / dev only. */}
            {canSim && environment === "SIMULATION" && <button onClick={doExitAll} data-testid="header-exit-all-btn"
              className="text-[11px] font-bold px-2.5 py-1.5 rounded-sm bg-[#FF3B30]/15 text-[#FF6b62] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 flex items-center gap-1">
              <Power className="h-3.5 w-3.5" /> EXIT ALL & STOP
            </button>}
            {canSim && <div className="relative">
              <button onClick={() => setShowNotif((s) => !s)} data-testid="notif-bell" className="relative p-1.5 text-slate-400 hover:text-white">
                <Bell className="h-5 w-5" />
                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#FF3B30] text-[9px] flex items-center justify-center font-bold">{unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-auto term-panel-2 rounded-sm z-50 shadow-2xl">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#2A2F3A] sticky top-0 bg-[#1A1D24]">
                    <span className="text-xs font-bold">Notifications</span>
                    <div className="flex items-center gap-2">
                      <button onClick={markRead} className="text-[10px] text-slate-400 hover:text-white">Mark all read</button>
                      <button onClick={() => setShowNotif(false)}><X className="h-3.5 w-3.5 text-slate-500" /></button>
                    </div>
                  </div>
                  {notifications.length === 0 && <div className="p-4 text-xs text-slate-500">No notifications yet.</div>}
                  {notifications.slice(0, 40).map((n) => (
                    <div key={n.id} className={`px-3 py-2 border-b border-[#20242c] text-xs ${!n.read ? "bg-[#12151C]" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full ${n.severity === "critical" || n.severity === "warning" ? "bg-[#FFCC00]" : n.severity === "success" ? "bg-[#00C805]" : "bg-[#2563EB]"}`} />
                        <span className="text-slate-200">{n.message}</span>
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5 mono pl-3.5">{new Date(n.created_at).toLocaleTimeString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>}
            <div className="flex items-center gap-2 pl-2 border-l border-[#2A2F3A]">
              <button type="button" onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                className="terminal-icon-button" data-testid="theme-toggle"
                aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`}
                title={`Use ${resolvedTheme === "dark" ? "light" : "dark"} theme`}>
                {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <NavLink to="/account" data-testid="header-account-link" className="text-xs text-slate-400 hover:text-white flex items-center gap-1" title={`My account · ${user?.name || user?.email}`}>
                <UserCircle2 className="h-4 w-4 2xl:hidden" />
                <span className="hidden 2xl:inline">{user?.name || user?.email}</span>
                {(user?.roles || []).includes("ADMIN") && <span className="hidden 2xl:inline text-[9px] font-bold text-[#2563EB]">ADMIN</span>}
              </NavLink>
              <button onClick={logout} data-testid="logout-btn" title="Sign out" className="p-1.5 text-slate-400 hover:text-[#FF3B30]"><LogOut className="h-4 w-4" /></button>
            </div>
          </div>
        </header>
        <ShadowHaltAlert halt={shadowHalt} />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
